import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Loader2, Palette } from "lucide-react";
import api from "../lib/api";

/**
 * Tegeltjes showroom — kant-en-klare templates die klanten met 1 klik kunnen openen
 * in de Tegel Designer. Verlaagt de drempel voor mensen zonder creatief idee.
 */
export default function TegeltjesPage() {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/custom-tile/templates")
            .then(({ data }) => setTemplates(data || []))
            .catch(() => setTemplates([]))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div data-testid="tegeltjes-page" className="min-h-screen" style={{ background: "var(--bg)" }}>
            {/* HERO */}
            <section className="hero-warm border-b border-[var(--border-soft)]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <p className="label-tiny" style={{ color: "var(--rose-deep)" }}>★ Tegeltjes showroom</p>
                        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl mt-3 leading-[1.05] tracking-tight">
                            Tegeltjes <span className="font-script" style={{ color: "var(--rose-deep)" }}>met karakter.</span>
                        </h1>
                        <p className="mt-6 max-w-xl text-base lg:text-lg leading-relaxed" style={{ color: "var(--text-2)" }}>
                            Geen idee? Begin bij een van onze favorieten en pas aan naar smaak. Of duik direct
                            in de tegel-ontwerper en bouw je eigen tekst, kleur en lettertype.
                        </p>
                        <div className="mt-7 flex flex-wrap gap-3">
                            <Link to="/tegel-designer" data-testid="tegeltjes-cta-designer" className="btn-rose">
                                <Sparkles size={14} /> Ontwerp je eigen <ArrowRight size={14} />
                            </Link>
                            <Link to="/tegeltjes#voorbeelden" data-testid="tegeltjes-cta-scroll" className="btn-ghost">
                                Voorbeelden bekijken ↓
                            </Link>
                        </div>
                    </div>

                    {/* Hero visual: stylized big tile */}
                    <div className="lg:col-span-5 flex justify-center">
                        <div className="relative w-full max-w-md aspect-square rounded-[2rem] shadow-[0_24px_60px_rgba(62,39,35,0.18)] flex items-center justify-center p-12 rotate-[-3deg] hover:rotate-0 transition-transform duration-500"
                            style={{ background: "linear-gradient(135deg, #FAE6DE 0%, #F5EDE0 50%, #E5D3B0 100%)" }}>
                            <span className="font-script text-6xl md:text-7xl leading-tight text-center" style={{ color: "var(--rose-deep)" }}>
                                Lieve<br />Mama
                            </span>
                            {/* Mini standaard */}
                            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-2/3 h-3 rounded-full" style={{ background: "var(--text)" }} />
                            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-1/3 h-1 rounded-full" style={{ background: "rgba(62,39,35,0.4)" }} />
                            <span className="absolute top-4 right-5 text-3xl rotate-12" style={{ color: "var(--gold)" }}>✦</span>
                            <span className="absolute bottom-8 left-5 text-2xl -rotate-12" style={{ color: "var(--rose)" }}>★</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* TEMPLATES GRID */}
            <section id="voorbeelden" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
                    <div>
                        <p className="label-tiny" style={{ color: "var(--rose-deep)" }}>★ Favorieten</p>
                        <h2 className="font-display text-4xl md:text-5xl mt-2 leading-tight">Geliefde ontwerpen.</h2>
                    </div>
                    <p className="text-xs max-w-xs" style={{ color: "var(--text-2)" }}>
                        Klik op een tegeltje om het direct te openen in de designer — daar pas je tekst en kleur naar wens aan.
                    </p>
                </div>

                {loading ? (
                    <div className="py-20 flex justify-center"><Loader2 size={28} className="animate-spin" style={{ color: "var(--rose-deep)" }} /></div>
                ) : templates.length === 0 ? (
                    <div className="text-center py-16 card-soft bg-white p-10">
                        <Palette size={36} style={{ color: "var(--rose-deep)" }} className="mx-auto mb-4" />
                        <p className="font-display text-2xl" style={{ color: "var(--text)" }}>Nog geen voorbeelden</p>
                        <p className="text-sm mt-2 max-w-md mx-auto" style={{ color: "var(--text-2)" }}>
                            We zijn nog onze eerste templates aan het bouwen. Wil je niet wachten? Ontwerp ondertussen je eigen tegel.
                        </p>
                        <Link to="/tegel-designer" data-testid="tegeltjes-empty-cta" className="btn-rose mt-6 inline-flex">
                            <Sparkles size={14} /> Eigen ontwerp maken
                        </Link>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="tegeltjes-grid">
                        {templates.map((t) => (
                            <TegelTemplateCard key={t.id} template={t} />
                        ))}
                    </div>
                )}
            </section>

            {/* CTA STRIP */}
            <section className="border-t border-[var(--border-soft)]" style={{ background: "var(--bg-warm)" }}>
                <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-12 text-center">
                    <p className="label-tiny" style={{ color: "var(--rose-deep)" }}>★ Iets unieks in gedachten?</p>
                    <h3 className="font-display text-3xl md:text-4xl mt-2">
                        Wij maken het graag <span className="font-script" style={{ color: "var(--rose-deep)" }}>op maat.</span>
                    </h3>
                    <p className="mt-3 max-w-xl mx-auto text-sm" style={{ color: "var(--text-2)" }}>
                        Voor speciale aanvragen — lange gedichten, foto-tegeltjes of bedrijfsbestellingen — gebruik je het Op-maat-formulier.
                    </p>
                    <div className="mt-6 flex justify-center gap-3">
                        <Link to="/maak-voor-mij" data-testid="tegeltjes-mvm-cta" className="btn-ghost">Vraag op maat aan →</Link>
                    </div>
                </div>
            </section>
        </div>
    );
}

const FONT_CSS = {
    playfair: "'Playfair Display', serif",
    serif: "Georgia, serif",
    script: "'Caveat', cursive",
    sans: "system-ui, sans-serif",
    elegant: "'DM Serif Display', serif",
    handwritten: "'Caveat', cursive",
};

function TegelTemplateCard({ template }) {
    const fontFamily = FONT_CSS[template.font] || FONT_CSS.playfair;
    const hasPhoto = !!template.image_url;
    return (
        <Link
            to={`/tegel-designer?template=${template.id}`}
            data-testid={`tegel-template-${template.id}`}
            className="group block">
            <div className="aspect-square rounded-3xl shadow-[0_8px_24px_rgba(62,39,35,0.08)] flex items-center justify-center p-6 border overflow-hidden transition-all group-hover:-translate-y-1 group-hover:shadow-[0_18px_42px_rgba(62,39,35,0.14)]"
                style={{
                    background: hasPhoto ? "#F5EDE0" : (template.tile_color || "#FAE6DE"),
                    borderColor: "rgba(0,0,0,0.06)",
                }}>
                {hasPhoto ? (
                    <img
                        src={template.image_url}
                        alt={template.name}
                        className="w-full h-full object-cover rounded-2xl"
                        onError={(e) => { e.target.style.display = "none"; }}
                    />
                ) : template.preview_data_url ? (
                    <img src={template.preview_data_url} alt={template.name} className="max-w-full max-h-full object-contain" />
                ) : (
                    <span
                        className="text-center leading-tight italic font-bold"
                        style={{
                            fontFamily,
                            color: template.text_color || "#3E2723",
                            fontSize: `${1.2 + (template.text_scale || 1) * 0.6}rem`,
                            transform: `scale(${template.text_scale || 1})`,
                            wordBreak: "break-word",
                        }}>
                        {template.text}
                    </span>
                )}
            </div>
            <div className="mt-3 flex items-center justify-between">
                <span className="font-display text-base" style={{ color: "var(--text)" }}>{template.name}</span>
                <span className="text-[10px] uppercase tracking-widest font-semibold inline-flex items-center gap-1 group-hover:gap-1.5 transition-all" style={{ color: "var(--rose-deep)" }}>
                    Open <ArrowRight size={11} />
                </span>
            </div>
            <div className="flex items-baseline justify-between mt-0.5">
                <p className="text-[11px]" style={{ color: "var(--text-3)" }}>{template.tile_color_name} · {template.text_color_name}</p>
                {template.price != null && (
                    <p className="text-xs font-mono font-bold" style={{ color: "var(--rose-deep)" }}>€ {Number(template.price).toFixed(2)}</p>
                )}
            </div>
        </Link>
    );
}
